import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MODULE_WORKSPACE_X } from "@/components/module_summary/moduleWorkspaceLayout";
import { cn } from "@/lib/utils";

export type ModuleSummaryStatTone = "default" | "muted" | "warning" | "success";

/** Compact KPI shown in an in-module summary strip (max 3 per strip). */
export type ModuleSummaryStat = {
  label: string;
  value: string | number;
  hint?: string;
  tone?: ModuleSummaryStatTone;
};

const STAT_VALUE_TONE_CLASS: Record<ModuleSummaryStatTone, string> = {
  default: "text-foreground",
  muted: "text-muted-foreground",
  warning: "text-amber-700 dark:text-amber-500",
  success: "text-emerald-700 dark:text-emerald-500",
};

export type ModuleSummaryStripProps = {
  stats?: ModuleSummaryStat[];
  loading?: boolean;
  maxStats?: number;
  action?: { label: string; onClick: () => void };
  lead?: ReactNode;
  trailing?: ReactNode;
  children?: ReactNode;
  className?: string;
  "data-testid"?: string;
};

/**
 * Unobtrusive one-row summary below a feature header.
 * Use for 2–3 counts or status chips—not full charts or Launch Pad–scale hero metrics.
 */
export function ModuleSummaryStrip({
  stats = [],
  loading = false,
  maxStats = 3,
  action,
  lead,
  trailing,
  children,
  className,
  "data-testid": testId = "module-summary-strip",
}: ModuleSummaryStripProps) {
  const displayStats = stats.slice(0, maxStats);
  const hasLeftContent = Boolean(lead) || displayStats.length > 0;
  const loadingLeft =
    lead || displayStats.length === 0 ? (
      <Skeleton className="h-4 w-56" />
    ) : (
      Array.from({ length: Math.max(displayStats.length, 2) }).map((_, i) => (
        <div key={i} className="space-y-1.5">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-5 w-10" />
        </div>
      ))
    );

  return (
    <div
      className={cn(
        "shrink-0 bg-muted/20 py-3",
        MODULE_WORKSPACE_X,
        className,
      )}
      data-testid={testId}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-6">
          {loading ? loadingLeft : (
            <>
              {lead}
              {displayStats.map((stat) => (
                <div key={stat.label} className="min-w-[4.5rem]">
                  <p className="text-xs font-medium text-muted-foreground">{stat.label}</p>
                  <p
                    className={cn(
                      "text-sm font-semibold tabular-nums tracking-tight",
                      STAT_VALUE_TONE_CLASS[stat.tone ?? "default"],
                    )}
                  >
                    {stat.value}
                  </p>
                  {stat.hint && (
                    <p className="text-xs text-muted-foreground/90">{stat.hint}</p>
                  )}
                </div>
              ))}
            </>
          )}
        </div>

        {(action || trailing) && !loading ? (
          <div className="flex min-w-0 flex-wrap items-center justify-end gap-2 sm:shrink-0">
            {action && !loading ? (
              <Button type="button" variant="outline" size="sm" onClick={action.onClick}>
                {action.label}
              </Button>
            ) : null}
            {trailing}
          </div>
        ) : null}
      </div>

      {children && (
        <div className={cn("text-xs text-muted-foreground", hasLeftContent && "mt-2")}>
          {children}
        </div>
      )}
    </div>
  );
}
